import axios from "axios";
import {ApiError} from "./ApiError";
import {apiRequest, getDeviceId, getHost, getTimeout} from "./ApiRequest";
import store from "../store";

const getAdminDetails = () => {
    return apiRequest("get", "/admin").then(response => {
        return Promise.resolve(response.data);
    });
};

// Register Method
const register = data => {
    data.deviceId = getDeviceId();
    return axios.post(getHost() + "/register", data).then(response => {
        localStorage.setItem("authUser", JSON.stringify(response.data));
        return Promise.resolve(response.data);
    }).catch(err => {
        return Promise.reject(new ApiError(err));
    });
};

// Login Method
const login = data => {
    data.deviceId = getDeviceId();
    data.timeout = getTimeout(data.timeout);
    return axios.post(getHost() + "/login", data).then(response => {
        localStorage.setItem("authUser", JSON.stringify(response.data));
        localStorage.removeItem("originalOrg");
        if (store.getState().Variables?.env?.API_ENV === "production" && response.data?.group === "admin") {
            localStorage.setItem("adminWarning", "THIS IS A LIVE PRODUCTION ENVIRONMENT WITH CLIENTS’ LIVE ASSETS.");
        }
        return Promise.resolve(response.data);
    }).catch(err => {
        return Promise.reject(new ApiError(err));
    });
};

const refreshToken = profile => {
    let user = JSON.parse(localStorage.getItem("authUser"));
    const data = {
        refreshToken: user.refreshToken,
        deviceId: getDeviceId(),
        timeout: getTimeout(),
        profile
    };
    return apiRequest("post", "/refreshToken", data).then(response => {
        localStorage.setItem("authUser", JSON.stringify(response.data));
        return Promise.resolve(response.data);
    });
};

// Change Email Method
const changeEmail = data => {
    data.deviceId = getDeviceId();
    data.timeout = getTimeout(data.timeout);
    return apiRequest("post", "/changeEmail", data).then(response => {
        localStorage.setItem("authUser", JSON.stringify(response.data));
        return Promise.resolve(response.data);
    });
};

// Reset Password Method
const resetPassword = data => {
    data.deviceId = getDeviceId();
    data.timeout = getTimeout(data.timeout);
    return axios.post(getHost() + "/resetPassword", data).then(response => {
        localStorage.setItem("authUser", JSON.stringify(response.data));
        return Promise.resolve(response.data);
    }).catch(err => {
        return Promise.reject(new ApiError(err));
    });
};

// Enable Mfa Method
const enableMfa = data => {
    data.deviceId = getDeviceId();
    data.timeout = getTimeout(data.timeout);
    return apiRequest("post", "/enableMfa", data).then(response => {
        localStorage.setItem("authUser", JSON.stringify(response.data));
        return Promise.resolve(response.data);
    });
};

// Disable Mfa Method
const disableMfa = data => {
    data.deviceId = getDeviceId();
    data.timeout = getTimeout(data.timeout);
    return apiRequest("post", "/disableMfa", data).then(response => {
        localStorage.setItem("authUser", JSON.stringify(response.data));
        return Promise.resolve(response.data);
    });
};

const sso = data => {
    return axios.post(getHost() + "/sso", data).then(response => {
        return Promise.resolve(response.data);
    }).catch(err => {
        return Promise.reject(new ApiError(err));
    });
};

// Client Onboard Method
const clientOnboard = data => {
    data.deviceId = getDeviceId();
    return axios.post(getHost() + "/clientOnboard", data).then(response => {
        return Promise.resolve(response.data);
    }).catch(err => {
        return Promise.reject(new ApiError(err));
    });
};

const getSessions = params => {
    return apiRequest("get", "/sessions", null, params).then(response => {
        return Promise.resolve(response.data);
    });
};

const inviteBitgo = data => {
    return apiRequest("post", "/invite-bitgo", data).then(response => {
        return Promise.resolve(response.data);
    });
};

const getCustodianSettings = (type, id, params) => {
    if (!type || !id) {
        return Promise.reject(new Error("Missing account info"));
    }
    return apiRequest("get", `/${type}/${id}/custodianSettings`, null, params).then(response => {
        return Promise.resolve(response.data);
    });
};

const getWalletsByToken = (type, id, token, force) => {
    if (!type || !id) {
        return Promise.reject(new Error("Missing account info"));
    }
    return apiRequest("get", `/${type}/${id}/wallets/${token}`, null, {_force: force}).then(response => {
        return Promise.resolve(response.data);
    });
};

const setWalletsByToken = (type, id, token, data) => {
    if (!type || !id) {
        return Promise.reject(new Error("Missing account info"));
    }
    return apiRequest("put", `/${type}/${id}/wallets/${token}`, data).then(response => {
        return Promise.resolve(response.data);
    });
};

const getFees = (type, id) => {
    if (!type || !id) {
        return Promise.reject(new Error("Missing account info"));
    }
    return apiRequest("get", `/${type}/${id}/fees`).then(response => {
        return Promise.resolve(response.data);
    });
};

const updateFeeAccount = (type, id, data) => {
    if (!type || !id) {
        return Promise.reject(new Error("Missing account info"));
    }
    return apiRequest("put", `/${type}/${id}/feeAccount`, data).then(response => {
        return Promise.resolve(response.data);
    });
};

const updateFees = (type, id, data) => {
    if (!type || !id) {
        return Promise.reject(new Error("Missing account info"));
    }
    return apiRequest("put", `/${type}/${id}/fees`, data).then(response => {
        return Promise.resolve(response.data);
    });
};

const updateCredentials = (type, id, data) => {
    if (!type || !id) {
        return Promise.reject(new Error("Missing account info"));
    }
    return apiRequest("put", `/${type}/${id}/credentials`, data).then(response => {
        return Promise.resolve(response.data);
    });
};

const getDelegators = params => {
    return apiRequest("get", "/delegators", null, params).then(response => {
        return Promise.resolve(response.data);
    });
};

const getDelegatees = params => {
    return apiRequest("get", "/delegatees", null, params).then(response => {
        return Promise.resolve(response.data);
    });
};

const updateDelegatees = data => {
    return apiRequest("post", "/delegatees", data).then(response => {
        return Promise.resolve(response.data);
    });
};

const delegateAccess = data => {
    return apiRequest("post", "/delegateAccess", data).then(response => {
        localStorage.setItem("authUser", JSON.stringify(response.data));
        return Promise.resolve(response.data);
    });
};

const deleteDelegatees = ids => {
    return apiRequest("delete", "/delegatees", ids).then(response => {
        return Promise.resolve(response.data);
    });
};

const updateDelegateePermissions = (id, data) => {
    return apiRequest("put", `/delegatees/${id}`, data).then(response => {
        return Promise.resolve(response.data);
    });
};

const getNotices = (type, id) => {
    if (!type || !id) {
        return Promise.reject(new Error("Missing account info"));
    }
    return apiRequest("get", `/${type}/${id}/notices`).then(response => {
        return Promise.resolve(response.data);
    });
};

const resendNotice = (type, id, noticeId) => {
    if (!type || !id) {
        return Promise.reject(new Error("Missing account info"));
    }
    return apiRequest("post", `/${type}/${id}/notices`, {noticeId}).then(response => {
        return Promise.resolve(response.data);
    });
};

export {
    getAdminDetails,
    register,
    login,
    refreshToken,
    changeEmail,
    resetPassword,
    enableMfa,
    disableMfa,
    clientOnboard,
    sso,
    getSessions,
    inviteBitgo,
    getCustodianSettings,
    getWalletsByToken,
    setWalletsByToken,
    getFees,
    updateFeeAccount,
    updateFees,
    updateCredentials,
    getDelegators,
    getDelegatees,
    updateDelegatees,
    delegateAccess,
    deleteDelegatees,
    updateDelegateePermissions,
    getNotices,
    resendNotice
};
